import React from 'react';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import StatePicker from '../../../components/StatePicker/StatePicker';
import { usertype_permissions } from '../../../enums/PermissionEnums';
import axios from 'axios';

// Import styles
import './memberSearch.scss';

// Import Zustand store
import { useAppStore } from '../../../stores';

interface Permission {
  name: string;
}

interface SearchPayloadType {
  memberId: string | null;
  badgeNumber: string | null;
  firstName: string;
  lastName: string;
  email?: string;
  affiliateState: string;
  affiliateStateNumber: string;
  affiliateStateName: string;
  affiliateStateType: string;
  affiliateNumber?: string;
  isDelegateOrAlternate: boolean;
  isRegistered: boolean;
  isDelegate: boolean;
}

interface IProps {
  setSearchClicked?: (clicked: boolean) => void;
  delegateOnly?: boolean;
  registeredOnly?: boolean;
}

const MemberSearchForm: React.FC<IProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width:600px)');

  // Zustand store
  const permissions = Object.values(useAppStore((state) => state.user.permissions)) as Permission[];
  const savedSearchPayload = useAppStore(
    (state) => state.search.memberSearchRequest
  ) as SearchPayloadType | null;
  const setMemberSearchPending = useAppStore((state) => state.setMemberSearchPending);
  const setMemberSearchSuccess = useAppStore((state) => state.setMemberSearchSuccess);
  const setMemberSearchError = useAppStore((state) => state.setMemberSearchError);
  const setMemberSearchRequest = useAppStore((state) => state.setMemberSearchRequest);

  React.useEffect(() => {
    // set if they are state fed president
    if (_.find(permissions, (p) => p.name == usertype_permissions.StateFed)) {
      setIsStateFedPresident(true);
    }

    // set if they are president or designee (treated the same)
    if (
      _.find(permissions, (p) =>
        p.name == usertype_permissions.AffiliatePresident ||
        p.name == usertype_permissions.Designee
      )
    ) {
      setIsAffiliatePresident(true);
    }

    // set if they are admin or backoffice
    if (
      _.find(permissions, (p) =>
        p.name == usertype_permissions.Admin ||
        p.name == usertype_permissions.BackOffice
      )
    ) {
      setIsAdminOrBackOffice(true);
    }
  }, []);

  const [searchPayload, setSearchPayload] = useState<SearchPayloadType>({
    memberId: null,
    badgeNumber: null,
    firstName: '',
    lastName: '',
    email: '',
    affiliateState: '',
    affiliateStateNumber: '',
    affiliateStateName: '',
    affiliateStateType: '',
    affiliateNumber: '',
    isDelegateOrAlternate: false,
    isRegistered: props.registeredOnly ? true : false,
    isDelegate: props.delegateOnly ? true : false,
  });

  // Check if any search field has a value (for button disabled state)
  const isSearchDisabled = () => {
    const { firstName, lastName, memberId, badgeNumber, affiliateState, affiliateStateName, affiliateNumber, email, isRegistered, isDelegateOrAlternate } = searchPayload;
    const hasValue = firstName || lastName || memberId || badgeNumber || affiliateState || affiliateStateName || affiliateNumber || email || isRegistered || isDelegateOrAlternate;
    return !hasValue || isAffiliateNbrError;
  };

  const updateSearchFields = (e: { target: { name: string; value: string; checked?: boolean } }) => {
    const { name, checked, value } = e.target;
    if (name === 'affiliateNumber') {
      let error = false;
      if (value.length === 0) {
        error = false;
      } else {
        const regexp = new RegExp(/^[a-zA-Z0-9]{5}$/);
        error = !String(value).match(regexp) ? true : false;
      }
      setIsAffiliateNbrError(error);
    }

    const payloadToSave = {
      ...searchPayload,
      [name]:
        name === 'isRegistered' ||
        name === 'isDelegate' ||
        name === 'isDelegateOrAlternate'
          ? checked
          : value,
    };
    setSearchPayload(payloadToSave);
    setMemberSearchRequest(payloadToSave);
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.setSearchClicked?.(true);
    setMemberSearchPending();
    axios
      .post('/api/Individual/search', searchPayload)
      .then((response) => {
        if (response && response.data) {
          const { data } = response;
          setMemberSearchSuccess(data);
        }
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.warn('Error: ' + error.message);
        } else {
          setMemberSearchError(error);
          enqueueSnackbar(`${error}. Please try again or contact support!`, {
            variant: 'error',
          });
        }
      });
  };

  const [isStateFedPresident, setIsStateFedPresident] = React.useState(false);

  const [isAffiliatePresident, setIsAffiliatePresident] = useState(false);

  const [isAdminOrBackOffice, setIsAdminOrBackOffice] = useState(false);

  const [isAffiliateNbrError, setIsAffiliateNbrError] = React.useState(false);

  const searchForm = () => {
    // Full form for all users (Badge Number only shown for Admin/BackOffice)
    return (
      <Box className="member-search-form">
        <Grid container spacing={2}>
          {/* Row 1: First Name | Last Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="First Name"
              name="firstName"
              onChange={updateSearchFields}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Last Name"
              name="lastName"
              onChange={updateSearchFields}
            />
          </Grid>

          {/* Row 2: Email | Member ID */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              name="email"
              onChange={updateSearchFields}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Member ID"
              name="memberId"
              onChange={updateSearchFields}
            />
          </Grid>

          {/* Row 3: Affiliate Name | Affiliate Number */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Affiliate Name"
              name="affiliateName"
              onChange={updateSearchFields}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Affiliate Number"
              name="affiliateNumber"
              onChange={updateSearchFields}
              error={isAffiliateNbrError}
              helperText={
                isAffiliateNbrError
                  ? "Please enter a 5 digit number (e.g. '00010' not '10')"
                  : ''
              }
            />
          </Grid>

          {/* Row 4: Affiliate State | Badge Number (Badge Number only for Admin/BackOffice) */}
          {!isStateFedPresident && (
            <Grid size={{ xs: 12, md: 6 }}>
              <StatePicker
                id={'member-search-affiliate-state-dropdown'}
                label={'Affiliate State'}
                defaultValue={''}
                displayDefaultValue={true}
                name={'affiliateState'}
                onChange={updateSearchFields}
              />
            </Grid>
          )}
          {isAdminOrBackOffice && (
            <Grid size={{ xs: 12, md: isStateFedPresident ? 12 : 6 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Badge Number"
                name="badgeNumber"
                onChange={updateSearchFields}
              />
            </Grid>
          )}

          {/* Search By and Button Row */}
          <Grid size={{ xs: 12 }}>
            <Box className="search-by-row">
              {props.delegateOnly || props.registeredOnly ? null : (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#7B818E',
                      letterSpacing: '0.15px',
                    }}
                  >
                    Search By:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          name="isRegistered"
                          onChange={updateSearchFields}
                        />
                      }
                      label="Registered"
                      sx={{ ml: 0, mr: 1, mt: { xs: 0, sm: '-9px' } }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          name="isDelegateOrAlternate"
                          onChange={updateSearchFields}
                        />
                      }
                      label="Delegate/Alternate"
                      sx={{ ml: 0, mt: { xs: 0, sm: '-9px' } }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Search Button - desktop: inline, mobile: hidden here (shown fixed at bottom) */}
          {!isMobile && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                className="search-members-btn"
                size="large"
                color="primary"
                variant="contained"
                type="submit"
                disabled={isSearchDisabled()}
                fullWidth
              >
                Search Members
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Mobile: Add padding at bottom to account for fixed button */}
        {isMobile && <Box sx={{ height: '80px' }} />}
      </Box>
    );
  };

  // Render the search button for mobile (fixed at bottom)
  const mobileSearchButton = () => {
    if (!isMobile) return null;
    return (
      <Box className="search-button-container-mobile">
        <Button
          className="search-members-btn"
          size="large"
          color="primary"
          variant="contained"
          type="submit"
          disabled={isSearchDisabled()}
          fullWidth
        >
          Search Members
        </Button>
      </Box>
    );
  };

  return (
    <form id="memberSearch" onSubmit={handleSubmit}>
      {searchForm()}
      {mobileSearchButton()}
    </form>
  );
};

export default MemberSearchForm;
